import { logger } from "../utils/logger";
import { useState, useEffect } from "react";
import axios from "axios";
import { Calendar, Clock, DollarSign, Layers, Gift, Trophy } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const dayOrder = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export default function Tournaments() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axios.get(`${API}/events`);
        // Sort by day order
        const sorted = response.data.sort((a, b) => 
          dayOrder.indexOf(a.day) - dayOrder.indexOf(b.day)
        );
        setEvents(sorted);
      } catch (err) {
        logger.error("Error fetching events:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center bg-casino-black">
        <div className="text-gold animate-pulse font-heading text-2xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20" data-testid="tournaments-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-casino-black to-casino-dark relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ 
            backgroundImage: "url('https://images.pexels.com/photos/2555292/pexels-photo-2555292.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')"
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 to-casino-dark" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <p className="text-gold uppercase tracking-[0.3em] text-sm mb-6 font-accent">
            Weekly Schedule
          </p>
          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl text-white mb-6">
            Tournament <span className="gold-gradient">Schedule</span>
          </h1>
          <p className="text-white/70 text-lg md:text-xl max-w-2xl mx-auto">
            Join us for exciting poker tournaments every day of the week. From casual college nights to $1K guarantee Saturdays.
          </p>
        </div>
      </section>

      {/* Tournament Cards */}
      <section className="py-20 bg-casino-black" data-testid="tournament-schedule">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {events.map((event, index) => (
              <div 
                key={event.id}
                className="glass-card overflow-hidden"
                data-testid={`tournament-${event.day.toLowerCase()}`}
              >
                {/* Day Header */}
                <div className="bg-gradient-to-r from-gold/20 to-transparent px-6 py-4 border-b border-white/10">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-gold" />
                      <span className="text-gold font-accent text-lg uppercase tracking-wider">
                        {event.day}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-white/60">
                      <Clock className="w-4 h-4" />
                      <span className="text-sm">{event.start_time}</span>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="font-heading text-2xl text-white mb-4">
                    {event.event_name}
                  </h3>
                  
                  <div className="space-y-3">
                    {event.buy_in && (
                      <div className="flex items-center gap-3">
                        <DollarSign className="w-5 h-5 text-gold shrink-0" />
                        <span className="text-white/80">{event.buy_in}</span>
                        {event.chips && (
                          <span className="text-white/50">• {event.chips}</span>
                        )}
                      </div>
                    )}
                    
                    {event.staff_bonus && (
                      <div className="flex items-center gap-3">
                        <Gift className="w-5 h-5 text-gold shrink-0" />
                        <span className="text-white/80">{event.staff_bonus}</span>
                      </div>
                    )}
                    
                    {event.add_ons && (
                      <div className="flex items-center gap-3">
                        <Layers className="w-5 h-5 text-gold shrink-0" />
                        <span className="text-white/80">{event.add_ons}</span>
                      </div>
                    )}
                    
                    {event.guarantee && (
                      <div className="flex items-center gap-3 mt-4 pt-4 border-t border-white/10">
                        <Trophy className="w-5 h-5 text-gold shrink-0" />
                        <span className="text-gold font-semibold">{event.guarantee}</span>
                      </div>
                    )}
                    
                    {event.description && !event.buy_in && (
                      <p className="text-white/60 mt-2">{event.description}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-20 bg-casino-dark">
        <div className="max-w-4xl mx-auto px-4">
          <div className="glass-card p-8 md:p-12 text-center">
            <Trophy className="w-16 h-16 text-gold mx-auto mb-6" />
            <h2 className="font-heading text-3xl md:text-4xl text-white mb-4">
              Tournament Information
            </h2>
            <div className="text-white/70 space-y-4 max-w-2xl mx-auto text-left">
              <p>
                <span className="text-gold font-semibold">Staff Bonus:</span> Support our staff with an optional $10 bonus that adds extra chips to your stack.
              </p>
              <p>
                <span className="text-gold font-semibold">Add-Ons:</span> Most tournaments allow up to 5 add-ons at $20 each, available at the start.
              </p>
              <p>
                <span className="text-gold font-semibold">Guarantees:</span> Our Saturday $1K and $500 guarantee tournaments ensure substantial prize pools.
              </p>
              <p>
                <span className="text-gold font-semibold">Location:</span> All tournaments are held at San Marcos Social Club.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
